import React, { useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2, Upload } from 'lucide-react'

/** Clipboard paste often yields unnamed blobs — give audit-friendly filenames. */
export function normalizePastedScreenshotFile(file) {
  if (!file) return null
  const type = file.type || 'image/png'
  const hasName = file.name && !/^(image\.(png|jpeg|jpg)|blob)$/i.test(file.name)
  if (hasName) return file
  const ext = type.includes('jpeg') || type.includes('jpg') ? 'jpg' : type.includes('webp') ? 'webp' : 'png'
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
  return new File([file], `screenshot-${stamp}.${ext}`, { type })
}

/**
 * Paste (Ctrl+V) or pick one screenshot — calls onImageFile immediately (one upload per action).
 */
export default function PasteScreenshotZone({
  onImageFile,
  disabled = false,
  uploading = false,
  compact = false,
  className = '',
}) {
  const fileInputRef = useRef(null)
  const [previewUrl, setPreviewUrl] = useState(null)

  useEffect(() => {
    if (!uploading && previewUrl) {
      const t = setTimeout(() => {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }, 400)
      return () => clearTimeout(t)
    }
    return undefined
  }, [uploading, previewUrl])

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const emitFile = (raw) => {
    if (disabled || uploading || !raw) return
    const file = normalizePastedScreenshotFile(raw)
    if (!file) return
    if (file.type?.startsWith('image/')) {
      const url = URL.createObjectURL(file)
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return url
      })
      onImageFile(file)
      return
    }
    onImageFile(file)
  }

  const handlePaste = (e) => {
    const items = e.clipboardData?.items
    if (!items || disabled || uploading) return
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) emitFile(file)
        break
      }
    }
  }

  if (compact) {
    return (
      <div className={`flex flex-wrap items-center gap-2 ${className}`}>
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            className="w-9 h-9 object-cover rounded border border-slate-200 flex-shrink-0"
          />
        ) : null}
        <div
          tabIndex={disabled || uploading ? -1 : 0}
          onPaste={handlePaste}
          className={`text-xs py-1.5 px-3 rounded-md border font-semibold flex items-center gap-1 outline-none focus:border-slate-400 ${
            disabled || uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-text hover:bg-white'
          }`}
          style={{ borderColor: '#cbd5e1', background: '#fff', color: '#475569' }}
          title="Click here, then paste a screenshot (Ctrl+V / Cmd+V)"
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
          Paste screenshot
        </div>
        <label
          className={`text-xs py-1.5 px-3.5 rounded-md border font-semibold flex items-center gap-1 ${
            disabled || uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          style={{ borderColor: '#0a5169', background: '#e8f2f6', color: '#0a5169' }}
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          Attach file
          <input
            type="file"
            className="hidden"
            disabled={disabled || uploading}
            accept="image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) emitFile(f)
              e.target.value = ''
            }}
          />
        </label>
      </div>
    )
  }

  return (
    <div
      tabIndex={disabled || uploading ? -1 : 0}
      onPaste={handlePaste}
      className={`py-5 px-4 text-center border-2 border-dashed rounded-lg outline-none transition-colors ${
        disabled || uploading
          ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
          : 'border-slate-200 text-slate-500 cursor-text hover:border-slate-300 hover:bg-slate-50 focus:border-slate-400'
      } ${className}`}
    >
      {uploading || previewUrl ? (
        <div className="flex flex-col items-center gap-2">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Screenshot preview"
              className="max-h-40 max-w-full object-contain rounded border border-slate-200 bg-white shadow-sm"
            />
          ) : (
            <Loader2 size={22} className="animate-spin text-slate-400" />
          )}
          <span className="text-xs inline-flex items-center gap-1.5">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : null}
            {uploading ? 'Uploading screenshot…' : 'Attached'}
          </span>
        </div>
      ) : (
        <>
          <Upload size={18} className="block mx-auto mb-1.5 text-slate-400" />
          <div className="text-xs font-semibold text-slate-600 mb-1">Paste screenshot here (Ctrl+V / Cmd+V)</div>
          <div className="text-[11px] text-slate-400 mb-3">Each paste uploads immediately — add one screenshot at a time.</div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
          >
            <ImagePlus size={13} />
            Or choose image / file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            disabled={disabled}
            accept="image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) emitFile(f)
              e.target.value = ''
            }}
          />
        </>
      )}
    </div>
  )
}
